import React, { useState, useEffect } from 'react';
import { Container, Box, CircularProgress, Alert } from '@mui/material';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from './firebase';
import TestGuidelines from './components/TestGuidelines';
import TestQuestion from './components/TestQuestion';
import TestCompleted from './components/TestCompleted';
import { getSections, getSectionIndex, getSecondsPerQuestion, initialSectionScores } from './testSections';

export default function Home() {
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTest = async () => {
      try {
        const userId = auth.currentUser?.uid;
        if (!userId) {
          setError('User not authenticated');
          return;
        }

        const testDoc = await getDoc(doc(db, 'tests', userId));
        
        if (testDoc.exists()) {
          setTest(testDoc.data());
        } else {
          setError('Test not found');
        }
      } catch (err) {
        setError('Failed to fetch test: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTest();
  }, []);

  const handleStartTest = async () => {
    try {
      const userId = auth.currentUser?.uid;
      const testRef = doc(db, 'tests', userId);
      const sections = getSections(test);
      const startedAt = new Date().toISOString();
      
      await updateDoc(testRef, {
        startedAt,
        currentIndex: 0,
        currentSectionIndex: 0,
        currentQuestionType: sections[0]?.key || 'aptitude',
        status: 'in_progress',
        ...initialSectionScores(sections)
      });

      setTest(prev => ({
        ...prev,
        startedAt,
        currentIndex: 0,
        currentSectionIndex: 0,
        currentQuestionType: sections[0]?.key || 'aptitude',
        status: 'in_progress',
        ...initialSectionScores(sections)
      }));
    } catch (err) {
      setError('Failed to start test: ' + err.message);
    }
  };

  const handleAnswer = async (answer) => {
    try {
      const userId = auth.currentUser?.uid;
      const testRef = doc(db, 'tests', userId);
      const sections = getSections(test);
      const section = sections[getSectionIndex(test, sections)];
      const currentQuestion = section?.questions?.[test.currentIndex];
      if (!section || !currentQuestion) {
        return;
      }
      
      const isCorrect = answer === currentQuestion.correctAnswer;
      const currentScore = Number(test[section.scoreKey]) || 0;
      const newScore = answer === 'skipped' ? currentScore : (isCorrect ? currentScore + 1 : currentScore - 0.5);

      const updateData = {
        [section.scoreKey]: newScore
      };

      await updateDoc(testRef, updateData);

      setTest(prev => ({
        ...prev,
        ...updateData
      }));
    } catch (err) {
      setError('Failed to save answer: ' + err.message);
    }
  };

  const handleNext = async () => {
    try {
      const userId = auth.currentUser?.uid;
      const testRef = doc(db, 'tests', userId);
      const sections = getSections(test);
      const sectionIndex = getSectionIndex(test, sections);
      const section = sections[sectionIndex];
      const newIndex = test.currentIndex + 1;
      
      if (newIndex >= section.questions.length) {
        if (sectionIndex < sections.length - 1) {
          const nextSection = sections[sectionIndex + 1];
          await updateDoc(testRef, {
            currentIndex: 0,
            currentSectionIndex: sectionIndex + 1,
            currentQuestionType: nextSection.key
          });

          setTest(prev => ({
            ...prev,
            currentIndex: 0,
            currentSectionIndex: sectionIndex + 1,
            currentQuestionType: nextSection.key
          }));
        } else {
          const completedAt = new Date().toISOString();
          await updateDoc(testRef, {
            completedAt,
            status: 'completed'
          });

          setTest(prev => ({
            ...prev,
            completedAt,
            status: 'completed'
          }));
        }
      } else {
        await updateDoc(testRef, {
          currentIndex: newIndex
        });

        setTest(prev => ({
          ...prev,
          currentIndex: newIndex
        }));
      }
    } catch (err) {
      setError('Failed to proceed to next question: ' + err.message);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Box mt={4}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Container>
    );
  }

  if (!test) {
    return (
      <Container maxWidth="md">
        <Box mt={4}>
          <Alert severity="warning">No test available</Alert>
        </Box>
      </Container>
    );
  }

  const sections = getSections(test);
  const sectionIndex = getSectionIndex(test, sections);
  const section = sections[sectionIndex];
  const question = section?.questions?.[test.currentIndex];

  return (
    <Container maxWidth="md">
      {test.status === 'not_started' ? (
        <TestGuidelines test={test} onStartTest={handleStartTest} />
      ) : test.status === 'in_progress' ? (
        question ? (
          <TestQuestion
            question={question}
            currentIndex={test.currentIndex}
            totalQuestions={section.questions.length}
            onAnswer={handleAnswer}
            onNext={handleNext}
            timePerQuestion={getSecondsPerQuestion(test)}
            sectionLabel={section.label}
            sectionKey={section.key}
            isLastQuestion={sectionIndex === sections.length - 1 && test.currentIndex === section.questions.length - 1}
          />
        ) : (
          <Box mt={4}>
            <Alert severity="error">Question not found</Alert>
          </Box>
        )
      ) : (
        <TestCompleted test={test} />
      )}
    </Container>
  );
}
